// src/services/LongRestService.js
import {LongRestGameApi} from '@/api/backendService';

/**
 * Calories, not "nutrition points". A full rest costs a day's food; half a
 * day buys the rest itself. The two numbers are the server's
 * (`apps/game/services/long_rest/offer.py`) and are mirrored here only so the
 * meter can draw its markers without a round trip.
 */
export const FULL_REST_CALORIES = 3000;
export const HALF_REST_CALORIES = FULL_REST_CALORIES / 2;

/**
 * LongRestService wraps the auto-generated LongRestApi. Components never call
 * the generated client directly.
 */
class LongRestService {
    /**
     * @param {LongRestApi} api The API client generated from the backend schema.
     */
    constructor(api = LongRestGameApi) {
        this.api = api;
    }

    /**
     * Every open rest offer belonging to the acting character.
     *
     * Endpoint: GET /long-rest/open_offers/
     *
     * @returns {Promise<Array>} The open offers, newest last.
     */
    async getOpenOffers() {
        return (await this.api.longRestOpenOffersList()).data;
    }

    /**
     * One offer, with its provisions expanded far enough to render them.
     *
     * Endpoint: GET /long-rest/open_offers/{id}/
     *
     * @param {string} offerId
     * @returns {Promise<Object>}
     */
    async getOfferDetails(offerId) {
        return (await this.api.longRestOpenOffersRetrieve(offerId)).data;
    }

    /**
     * Open a rest offer directly, cancelling any earlier open one.
     *
     * Endpoint: POST /long-rest/open_offers/open/
     *
     * @returns {Promise<Object>}
     */
    async openOffer() {
        return (await this.api.longRestOpenOffersOpenCreate()).data;
    }

    /**
     * Put a food item on the table.
     *
     * Endpoint: POST /long-rest/open_offers/{offer_pk}/provisions/
     *
     * @param {string} offerId
     * @param {string} worldItemId The world item id, not the definition id.
     * @returns {Promise<Object>}
     */
    async addProvision(offerId, worldItemId) {
        return (await this.api.longRestOpenOffersProvisionsCreate(offerId, {world_item: worldItemId})).data;
    }

    /**
     * Take a food item back off the table.
     *
     * Endpoint: DELETE /long-rest/open_offers/{offer_pk}/provisions/{world_item_id}/
     *
     * @param {string} offerId
     * @param {string} worldItemId
     * @returns {Promise<void>}
     */
    async removeProvision(offerId, worldItemId) {
        return (await this.api.longRestOpenOffersProvisionsDestroy(offerId, worldItemId)).data;
    }

    /**
     * Eat the provisions and take the rest. Rejected below half a day's food.
     *
     * Endpoint: POST /long-rest/open_offers/{id}/confirm/
     *
     * @param {string} offerId
     * @returns {Promise<Object>}
     */
    async confirmOffer(offerId) {
        return (await this.api.longRestOpenOffersConfirmCreate(offerId)).data;
    }

    /**
     * Walk away. The food is never taken, so nothing is returned.
     *
     * Endpoint: POST /long-rest/open_offers/{id}/cancel/
     *
     * @param {string} offerId
     * @returns {Promise<Object>}
     */
    async cancelOffer(offerId) {
        return (await this.api.longRestOpenOffersCancelCreate(offerId)).data;
    }
}

export default new LongRestService(LongRestGameApi);
